import React, { useState, useRef } from 'react';
import { 
  Bell, Heart, MessageCircle, UserPlus, Trash2, 
  CheckCheck, ChevronRight 
} from 'lucide-react';

export const NotificationItem = ({ 
  notification, 
  onDelete, 
  onClick,
  onSwipeDelete 
}) => {
  const [startX, setStartX] = useState(0);
  const [currentX, setCurrentX] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const containerRef = useRef(null);

  const SWIPE_THRESHOLD = 100;

  const handleTouchStart = (e) => {
    setStartX(e.touches[0].clientX);
    setIsSwiping(true);
  };

  const handleTouchMove = (e) => {
    if (!isSwiping) return;
    const diff = e.touches[0].clientX - startX;
    // Solo permitir swipe hacia la derecha
    if (diff > 0) {
      setCurrentX(Math.min(diff, 150));
      setShowDelete(diff > SWIPE_THRESHOLD);
    }
  };

  const handleTouchEnd = () => {
    if (currentX > SWIPE_THRESHOLD && onSwipeDelete) {
      onSwipeDelete(notification.id);
    }
    setCurrentX(0);
    setIsSwiping(false);
    setShowDelete(false);
  };

  const handleMouseDown = (e) => {
    setStartX(e.clientX);
    setIsSwiping(true);
  };

  const handleMouseMove = (e) => {
    if (!isSwiping) return;
    const diff = e.clientX - startX;
    if (diff > 0) {
      setCurrentX(Math.min(diff, 150));
      setShowDelete(diff > SWIPE_THRESHOLD);
    }
  };

  const handleMouseUp = () => {
    if (currentX > SWIPE_THRESHOLD && onSwipeDelete) {
      onSwipeDelete(notification.id);
    }
    setCurrentX(0);
    setIsSwiping(false);
    setShowDelete(false);
  };

  const handleMouseLeave = () => {
    if (isSwiping) {
      setCurrentX(0);
      setIsSwiping(false);
      setShowDelete(false);
    }
  };

  const getIcon = () => {
    switch (notification.type) {
      case 'follow':
        return <UserPlus size={20} className="text-primary" />;
      case 'like':
        return <Heart size={20} fill="currentColor" className="text-rose-500" />;
      case 'comment':
      case 'reply':
        return <MessageCircle size={20} className="text-blue-500" />;
      default:
        return <Bell size={20} className="text-stone-400" />;
    }
  };

  const getTypeLabel = () => {
    switch (notification.type) {
      case 'follow':
        return 'Nuevo seguidor';
      case 'like':
        return 'Me gusta';
      case 'comment':
        return 'Comentario';
      case 'reply':
        return 'Respuesta';
      default:
        return 'Notificación';
    }
  };

  return (
    <div 
      ref={containerRef}
      className="relative overflow-hidden rounded-[32px]"
    >
      {/* Delete background */}
      <div 
        className={`absolute inset-0 bg-rose-500 flex items-center px-6 transition-opacity ${
          showDelete ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <Trash2 size={24} className="text-white" />
        <span className="ml-3 text-white font-black uppercase text-[10px] tracking-widest">
          Eliminar
        </span>
      </div>

      {/* Notification card */}
      <div
        className={`relative bg-white dark:bg-stone-900 p-6 border border-stone-100 dark:border-stone-800 flex items-center gap-4 shadow-sm cursor-pointer transition-all ${
          !notification.is_read ? 'border-l-4 border-l-primary' : ''
        }`}
        style={{
          transform: `translateX(${currentX}px)`,
          transition: isSwiping ? 'none' : 'transform 0.3s ease'
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onClick={() => !isSwiping && onClick && onClick(notification)}
      >
        {/* Actor Avatar */}
        <img 
          src={notification.actor_avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${notification.actor_id}`}
          className="w-14 h-14 rounded-2xl object-cover shadow-md flex-shrink-0"
          alt="Avatar"
        />

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[9px] font-black uppercase tracking-widest text-primary bg-primary/10 px-2 py-0.5 rounded-md">
              {getTypeLabel()}
            </span>
            {!notification.is_read && (
              <span className="w-2 h-2 bg-primary rounded-full animate-pulse" />
            )}
          </div>
          <p className="font-bold dark:text-white truncate">
            <span className="text-primary">@{notification.actor_username || notification.actor_name}</span>
            {' '}{notification.message?.replace(notification.actor_name, '').trim() || ''}
          </p>
          <p className="text-[10px] text-stone-400 font-bold mt-1">
            {formatTimeAgo(notification.created_at)}
          </p>
        </div>

        {/* Icon */}
        <div className="w-12 h-12 bg-stone-50 dark:bg-stone-800 rounded-xl flex items-center justify-center flex-shrink-0">
          {getIcon()}
        </div>

        {/* Arrow indicator */}
        <ChevronRight size={16} className="text-stone-300 flex-shrink-0" />
      </div>
    </div>
  );
};

// Helper function para formatear tiempo
function formatTimeAgo(dateString) {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);

  if (diffInSeconds < 60) return 'Ahora mismo';
  if (diffInSeconds < 3600) return `Hace ${Math.floor(diffInSeconds / 60)} min`;
  if (diffInSeconds < 86400) return `Hace ${Math.floor(diffInSeconds / 3600)} h`;
  if (diffInSeconds < 604800) return `Hace ${Math.floor(diffInSeconds / 86400)} días`;
  return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
}

export const NotificationList = ({ 
  notifications, 
  onDelete, 
  onClick, 
  onMarkAllRead,
  hasUnread 
}) => {
  if (notifications.length === 0) {
    return (
      <div className="text-center py-32">
        <div className="w-24 h-24 bg-stone-100 dark:bg-stone-800 rounded-full flex items-center justify-center mx-auto mb-6">
          <Bell size={40} className="text-stone-300" />
        </div>
        <h3 className="text-xl font-black heading-serif dark:text-white mb-2">
          Sin notificaciones
        </h3>
        <p className="text-stone-400 font-medium">
          Cuando otros chefs interactúen contigo, lo verás aquí
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with mark all as read */}
      {hasUnread && (
        <div className="flex justify-end mb-4">
          <button
            onClick={onMarkAllRead}
            className="flex items-center gap-2 px-4 py-2 text-[10px] font-black uppercase tracking-widest text-primary hover:bg-primary/10 rounded-xl transition-all"
          >
            <CheckCheck size={16} /> Marcar todo como leído
          </button>
        </div>
      )}

      {/* Swipe hint */}
      <p className="text-[10px] text-stone-400 font-bold text-center mb-4">
        Desliza hacia la derecha para eliminar
      </p>

      {/* Notifications */}
      {notifications.map(notification => (
        <NotificationItem
          key={notification.id}
          notification={notification}
          onDelete={onDelete}
          onClick={onClick}
          onSwipeDelete={onDelete}
        />
      ))}
    </div>
  );
};

export default NotificationList;
